import React, { useEffect, useState, useRef } from "react";
import { Link } from "react-router-dom";
import { api } from "@/api/client";
import { Bell } from "lucide-react";

/**
 * Sidebar notification bell — polls /api/notifications every 45s.
 * Click opens a dropdown showing the 10 most recent. In-app only (no push).
 */
export default function NotificationBell() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const dropdownRef = useRef(null);

  const load = async () => {
    try {
      const { data } = await api.get("/notifications");
      setItems(data);
    } catch (_) { /* noop */ }
  };

  useEffect(() => {
    load();
    const t = setInterval(load, 45000);
    return () => clearInterval(t);
  }, []);

  useEffect(() => {
    const onClickAway = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) setOpen(false);
    };
    if (open) document.addEventListener("mousedown", onClickAway);
    return () => document.removeEventListener("mousedown", onClickAway);
  }, [open]);

  const unread = items.filter((n) => !n.read).length;

  const markAllRead = async () => {
    await api.post("/notifications/mark-all-read");
    load();
  };
  const markOne = async (id) => {
    await api.post(`/notifications/${id}/read`);
    load();
  };

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        data-testid="notification-bell"
        className="relative flex items-center gap-3 w-full px-4 py-2.5 rounded-sm text-sm font-semibold text-slate-600 hover:bg-slate-50 hover:text-slate-900"
      >
        <Bell className="w-4 h-4" strokeWidth={2.25} />
        Notifications
        {unread > 0 && (
          <span data-testid="notification-count"
            className="ml-auto text-[10px] font-black bg-orange-500 text-white rounded-full w-5 h-5 flex items-center justify-center">
            {unread > 9 ? "9+" : unread}
          </span>
        )}
      </button>

      {open && (
        <div className="absolute left-full ml-2 top-0 z-50 w-80 bg-white border border-slate-200 rounded-sm shadow-xl" data-testid="notification-dropdown">
          <div className="flex items-center justify-between px-4 py-2 border-b border-slate-200">
            <div className="text-xs font-bold text-slate-700">Notifications</div>
            {unread > 0 && (
              <button onClick={markAllRead} className="text-[11px] text-orange-600 hover:text-orange-700 font-bold" data-testid="notif-mark-all">
                Mark all read
              </button>
            )}
          </div>
          <ul className="max-h-96 overflow-y-auto">
            {items.length === 0 ? (
              <li className="px-4 py-8 text-center text-sm text-slate-500">No notifications yet.</li>
            ) : (
              items.slice(0, 20).map((n) => (
                <li key={n.id} className={`px-4 py-3 border-b border-slate-100 hover:bg-slate-50 ${!n.read ? "bg-orange-50/40" : ""}`}>
                  <NotifBody n={n} onRead={() => markOne(n.id)} />
                </li>
              ))
            )}
          </ul>
        </div>
      )}
    </div>
  );
}

function NotifBody({ n, onRead }) {
  const to = n.context === "enquiry" && n.enquiry_id ? `/enquiries/${n.enquiry_id}` : null;
  const Wrapper = to ? Link : "div";
  const props = to ? { to, onClick: onRead } : {};
  return (
    <Wrapper {...props} className="block">
      <div className="flex items-start gap-2">
        {!n.read && <span className="w-1.5 h-1.5 rounded-full bg-orange-500 mt-1.5 shrink-0" />}
        <div className="flex-1 min-w-0">
          <div className="font-bold text-sm text-slate-900 truncate">{n.title}</div>
          <div className="text-xs text-slate-600 mt-0.5 line-clamp-2">{n.body}</div>
          <div className="text-[10px] text-slate-400 mt-1">{new Date(n.created_at).toLocaleString()}</div>
        </div>
      </div>
    </Wrapper>
  );
}
